import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gamepad2, Wallet, Trophy } from "lucide-react";
import { useLang, STR } from "@/i18n";

const BOARD = ["X", null, "O", null, "X", null, "O", null, null];

const RANK = [
  { pos: 1, name: "Felipe", wins: 128 },
  { pos: 2, name: "Maria", wins: 115 },
  { pos: 3, name: "Você", wins: 102, me: true },
  { pos: 4, name: "Ana", wins: 96 },
];

const Phone = ({ children }) => (
  <div
    data-testid="app-preview-phone"
    className="relative mx-auto w-[290px] sm:w-[320px] rounded-[2.8rem] border border-white/15 bg-[#0c1122] p-3 shadow-[0_40px_120px_-30px_rgba(46,107,255,0.4)]"
  >
    <div className="rounded-[2.2rem] bg-[#06091a] overflow-hidden">
      <div className="flex justify-center py-2.5">
        <div className="w-20 h-4 rounded-full bg-black border border-white/10" />
      </div>
      <div className="px-5 pb-7 min-h-[400px]">{children}</div>
    </div>
  </div>
);

function PartidaScreen() {
  const { lang } = useLang();
  const t = STR[lang];
  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold text-white">{t.you}</p>
          <p className="mono text-[9px] text-sky-300">X · 400 {t.credits_word}</p>
        </div>
        <span className="mono text-[9px] uppercase tracking-[0.25em] text-slate-500">vs</span>
        <div className="text-right">
          <p className="text-xs font-bold text-white">Maria</p>
          <p className="mono text-[9px] text-amber-300">O · 100 {t.credits_word}</p>
        </div>
      </div>
      <div className="rounded-xl border border-amber-400/25 bg-amber-400/5 py-2 text-center">
        <p className="mono text-[9px] uppercase tracking-widest text-amber-300">{t.in_dispute}</p>
        <p className="font-display font-bold text-white text-sm">{t.credits10}</p>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {BOARD.map((c, i) => (
          <div
            key={i}
            className="aspect-square rounded-xl border border-white/10 bg-white/[0.03] grid place-items-center"
          >
            {c && (
              <span
                className={`font-display font-black text-2xl ${
                  c === "X" ? "text-sky-300 text-glow-blue" : "text-amber-400 text-glow-gold"
                }`}
              >
                {c}
              </span>
            )}
          </div>
        ))}
      </div>
      <div>
        <div className="flex justify-between mb-1.5">
          <p className="mono text-[9px] uppercase tracking-widest text-slate-400">{t.scr_turn}</p>
          <p className="mono text-[9px] text-sky-300">12s</p>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div
            initial={{ width: "100%" }}
            animate={{ width: "38%" }}
            transition={{ duration: 8, repeat: Infinity, repeatType: "reverse", ease: "linear" }}
            className="h-full rounded-full bg-gradient-to-r from-sky-400 to-amber-400"
          />
        </div>
      </div>
    </div>
  );
}

function CarteiraScreen() {
  const { lang } = useLang();
  const t = STR[lang];
  return (
    <div className="flex flex-col gap-4 pt-2">
      <p className="mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{t.scr_wallet}</p>
      <div>
        <p className="mono text-[9px] uppercase tracking-widest text-slate-400">{t.scr_total}</p>
        <p className="font-display font-black text-4xl text-white">R$ 75<span className="text-slate-500 text-2xl">,00</span></p>
      </div>
      <div className="h-2.5 rounded-full overflow-hidden flex">
        <div className="bg-sky-400" style={{ width: "66%" }} />
        <div className="bg-amber-400" style={{ width: "34%" }} />
      </div>
      <div className="flex flex-col gap-2.5">
        {[
          [t.scr_dep, "R$ 50,00", "text-sky-300", "bg-sky-400"],
          [t.scr_profit, "R$ 25,00", "text-amber-300", "bg-amber-400"],
          [t.scr_fee, `600 ${t.credits_word}`, "text-white", "bg-white"],
        ].map(([l, v, cls, dot]) => (
          <div key={l} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5">
            <span className="flex items-center gap-2 text-xs text-slate-400">
              <span className={`w-2 h-2 rounded-full ${dot}`} />
              {l}
            </span>
            <span className={`text-xs font-bold ${cls}`}>{v}</span>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-gradient-to-r from-sky-400 via-blue-500 to-amber-400 text-black text-center font-display font-bold text-sm py-3.5 mt-1">
        {t.scr_withdraw}
      </div>
    </div>
  );
}

function RankingScreen() {
  const { lang } = useLang();
  const t = STR[lang];
  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="flex items-center justify-between">
        <p className="mono text-[9px] uppercase tracking-[0.25em] text-slate-500">{t.scr_rank}</p>
        <span className="mono text-[9px] uppercase tracking-widest text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded-full px-2.5 py-1">
          {t.scr_weekly}
        </span>
      </div>
      {RANK.map((r) => (
        <div
          key={r.pos}
          className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 ${
            r.me
              ? "border-amber-400/40 bg-amber-400/10"
              : "border-white/10 bg-white/[0.03]"
          }`}
        >
          <span
            className={`font-display font-black text-lg w-7 ${
              r.pos === 1 ? "text-amber-300" : r.pos === 2 ? "text-slate-300" : r.pos === 3 ? "text-orange-300" : "text-slate-500"
            }`}
          >
            {r.pos}
          </span>
          <div className="flex-1">
            <p className={`text-xs font-bold ${r.me ? "text-amber-300" : "text-white"}`}>
              {r.me ? t.you : r.name}
            </p>
            <p className="mono text-[9px] text-slate-500">{r.wins} {t.scr_wins}</p>
          </div>
          {r.pos === 1 && <Trophy className="w-4 h-4 text-amber-300" />}
        </div>
      ))}
      <p className="mono text-[9px] text-center uppercase tracking-widest text-slate-500 mt-1">
        {t.scr_top3}
      </p>
    </div>
  );
}

export default function AppPreview() {
  const { lang } = useLang();
  const t = STR[lang];
  const [tab, setTab] = useState("partida");
  const tabs = [
    { id: "partida", label: t.tab_match, icon: Gamepad2 },
    { id: "carteira", label: t.tab_wallet, icon: Wallet },
    { id: "ranking", label: t.tab_ranking, icon: Trophy },
  ];

  return (
    <section id="preview" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-1/3 -left-40 w-[30rem] h-[30rem] rounded-full bg-blue-700/10 blur-[150px] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="mono text-xs uppercase tracking-[0.3em] text-slate-500 mb-4">
            {t.prev_eyebrow}
          </p>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
            {t.prev_h2a}{" "}
            <span className="bg-gradient-to-r from-sky-300 to-amber-300 bg-clip-text text-transparent">
              {t.prev_h2b}
            </span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-md">
            {t.prev_sub}
          </p>
          <div className="mt-8 flex gap-2.5 flex-wrap">
            {tabs.map((tb) => {
              const active = tab === tb.id;
              return (
                <button
                  key={tb.id}
                  onClick={() => setTab(tb.id)}
                  data-testid={`preview-tab-${tb.id}`}
                  className={`relative flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold transition-colors duration-200 border ${
                    active
                      ? "text-black border-transparent"
                      : "text-slate-400 border-white/10 hover:text-white hover:border-white/25"
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="preview-tab-pill"
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-sky-400 to-amber-400"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                  <tb.icon className="relative w-4 h-4" strokeWidth={2.4} />
                  <span className="relative">{tb.label}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          <Phone>
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3 }}
              >
                {tab === "partida" && <PartidaScreen />}
                {tab === "carteira" && <CarteiraScreen />}
                {tab === "ranking" && <RankingScreen />}
              </motion.div>
            </AnimatePresence>
          </Phone>
        </motion.div>
      </div>
    </section>
  );
}
